import React, { useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  useTheme,
  IconButton,
  Grid,
  Card,
  CardContent,
  Chip,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import PrintIcon from '@mui/icons-material/Print';
import GetAppIcon from '@mui/icons-material/GetApp';
import {
  Contract,
  ContractBudget,
  AssociatedEstimate,
  AssociatedChangeOrder,
} from '../../api/protosCompiled/contract/contract_pb';
import { acceptedEstimateId } from '../../utils/useLockBid';
import { getStatusChipProps } from '../estimates/ListEstimates';
import { alpha } from '@mui/material/styles';

interface ContractBudgetReportProps {
  open: boolean;
  onClose: () => void;
  contract: Contract.AsObject;
}

interface BudgetSummary {
  totalLaborHours: number;
  totalDirectLaborCost: number;
  totalIndirectLaborCost: number;
  totalMaterialCost: number;
  totalQuotedMaterialCost: number;
  totalGeneralExpenseCost: number;
  totalEquipmentCost: number;
  totalSubcontractorCost: number;
  totalSellingPrice: number;
}

export const ContractBudgetReport: React.FC<ContractBudgetReportProps> = ({
  open,
  onClose,
  contract,
}) => {
  const theme = useTheme();

  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  };

  const formatHours = (hours: number): string => {
    return `${hours.toFixed(2)} hrs`;
  };

  // Helper function to safely render HTML content
  const renderHtmlContent = (htmlString: string | undefined | null): React.ReactNode => {
    if (!htmlString) return null;

    // Remove common HTML entities and clean up the content
    const cleanedHtml = htmlString
      .replace(/&nbsp;/g, ' ')
      .replace(/&amp;/g, '&')
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
      .replace(/&quot;/g, '"')
      .replace(/&#39;/g, "'");

    // Check if content contains HTML tags
    const containsHtml = /<[^>]*>/g.test(cleanedHtml);

    if (containsHtml) {
      return (
        <span
          dangerouslySetInnerHTML={{ __html: cleanedHtml }}
          style={{
            wordBreak: 'break-word',
            lineHeight: 1.5,
          }}
        />
      );
    }

    // If no HTML tags, return as plain text
    return cleanedHtml;
  };

  // Helper function for print version HTML content
  const renderPrintHtmlContent = (htmlString: string | undefined | null): string => {
    if (!htmlString) return '';

    return htmlString
      .replace(/&nbsp;/g, ' ')
      .replace(/&amp;/g, '&')
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
      .replace(/&quot;/g, '"')
      .replace(/&#39;/g, "'");
  };

  // Helper function to get print-friendly status colors
  const getPrintStatusColors = (statusName: string) => {
    const statusLower = statusName.toLowerCase();

    if (statusLower === 'pending') {
      return {
        backgroundColor: '#fff3cd',
        color: '#856404',
        borderColor: '#ffeaa7',
      };
    }

    if (statusLower === 'accepted') {
      return {
        backgroundColor: '#d4edda',
        color: '#155724',
        borderColor: '#c3e6cb',
      };
    }

    if (statusLower === 'rejected') {
      return {
        backgroundColor: '#f8d7da',
        color: '#721c24',
        borderColor: '#f5c6cb',
      };
    }

    if (statusLower === 'void') {
      return {
        backgroundColor: '#e2e3e5',
        color: '#383d41',
        borderColor: '#d1d3d4',
      };
    }

    if (
      statusLower.includes('progress') ||
      statusLower.includes('active') ||
      statusLower.includes('working')
    ) {
      return {
        backgroundColor: '#cce7ff',
        color: '#004085',
        borderColor: '#81c784',
      };
    }

    if (
      statusLower.includes('completed') ||
      statusLower.includes('finished') ||
      statusLower.includes('done')
    ) {
      return {
        backgroundColor: '#d4edda',
        color: '#155724',
        borderColor: '#c3e6cb',
      };
    }

    if (
      statusLower.includes('hold') ||
      statusLower.includes('paused') ||
      statusLower.includes('suspended')
    ) {
      return {
        backgroundColor: '#e2e3e5',
        color: '#383d41',
        borderColor: '#d1d3d4',
      };
    }

    if (statusLower.includes('draft') || statusLower.includes('new')) {
      return {
        backgroundColor: '#f8f9fa',
        color: '#495057',
        borderColor: '#dee2e6',
      };
    }

    // Default styling for unknown statuses
    return {
      backgroundColor: '#e9ecef',
      color: '#495057',
      borderColor: '#ced4da',
    };
  };

  const getBudgetSellingPrice = (budget?: ContractBudget.AsObject): number => {
    if (!budget) return 0;
    return budget.budgetsellingprice && budget.budgetsellingprice > 0
      ? budget.budgetsellingprice
      : budget.budgettotalestimate || 0;
  };

  const getBudgetCost = (budget?: ContractBudget.AsObject): number => {
    if (!budget) return 0;
    return (
      (budget.budgetdirectlaborcost || 0) +
      (budget.budgetindirectlaborcost || 0) +
      (budget.budgetmaterialcost || 0) +
      (budget.budgetquotedmaterialcost || 0) +
      (budget.budgetgeneralexpensecost || 0) +
      (budget.budgetequipmentcost || 0) +
      (budget.budgetsubcontractorcost || 0)
    );
  };

  const calculateEstimateBudgetSummary = (
    estimatebudget: ContractBudget.AsObject | undefined
  ): BudgetSummary => {
    return {
      totalLaborHours: estimatebudget?.budgetlaborhours || 0,
      totalDirectLaborCost: estimatebudget?.budgetdirectlaborcost || 0,
      totalIndirectLaborCost: estimatebudget?.budgetindirectlaborcost || 0,
      totalMaterialCost: estimatebudget?.budgetmaterialcost || 0,
      totalQuotedMaterialCost: estimatebudget?.budgetquotedmaterialcost || 0,
      totalGeneralExpenseCost: estimatebudget?.budgetgeneralexpensecost || 0,
      totalEquipmentCost: estimatebudget?.budgetequipmentcost || 0,
      totalSubcontractorCost: estimatebudget?.budgetsubcontractorcost || 0,
      totalSellingPrice: getBudgetSellingPrice(estimatebudget),
    };
  };

  const calculateApprovedChangeOrdersBudgetSummary = (
    changeOrders: AssociatedChangeOrder.AsObject[]
  ): BudgetSummary => {
    return changeOrders
      .filter(changeOrder => changeOrder.changeorderstatusid === acceptedEstimateId)
      .reduce(
        (acc, changeOrder) => {
          const budget = changeOrder.changeorderbudget;
          return {
            totalLaborHours: acc.totalLaborHours + (budget?.budgetlaborhours || 0),
            totalDirectLaborCost: acc.totalDirectLaborCost + (budget?.budgetdirectlaborcost || 0),
            totalIndirectLaborCost:
              acc.totalIndirectLaborCost + (budget?.budgetindirectlaborcost || 0),
            totalMaterialCost: acc.totalMaterialCost + (budget?.budgetmaterialcost || 0),
            totalQuotedMaterialCost:
              acc.totalQuotedMaterialCost + (budget?.budgetquotedmaterialcost || 0),
            totalGeneralExpenseCost:
              acc.totalGeneralExpenseCost + (budget?.budgetgeneralexpensecost || 0),
            totalEquipmentCost: acc.totalEquipmentCost + (budget?.budgetequipmentcost || 0),
            totalSubcontractorCost:
              acc.totalSubcontractorCost + (budget?.budgetsubcontractorcost || 0),
            totalSellingPrice: acc.totalSellingPrice + getBudgetSellingPrice(budget),
          };
        },
        {
          totalLaborHours: 0,
          totalDirectLaborCost: 0,
          totalIndirectLaborCost: 0,
          totalMaterialCost: 0,
          totalQuotedMaterialCost: 0,
          totalGeneralExpenseCost: 0,
          totalEquipmentCost: 0,
          totalSubcontractorCost: 0,
          totalSellingPrice: 0,
        }
      );
  };

  const calculateNonApprovedChangeOrdersBudgetSummary = (
    changeOrders: AssociatedChangeOrder.AsObject[]
  ): BudgetSummary => {
    return changeOrders
      .filter(changeOrder => changeOrder.changeorderstatusid !== acceptedEstimateId)
      .reduce(
        (acc, changeOrder) => {
          const budget = changeOrder.changeorderbudget;
          return {
            totalLaborHours: acc.totalLaborHours + (budget?.budgetlaborhours || 0),
            totalDirectLaborCost: acc.totalDirectLaborCost + (budget?.budgetdirectlaborcost || 0),
            totalIndirectLaborCost:
              acc.totalIndirectLaborCost + (budget?.budgetindirectlaborcost || 0),
            totalMaterialCost: acc.totalMaterialCost + (budget?.budgetmaterialcost || 0),
            totalQuotedMaterialCost:
              acc.totalQuotedMaterialCost + (budget?.budgetquotedmaterialcost || 0),
            totalGeneralExpenseCost:
              acc.totalGeneralExpenseCost + (budget?.budgetgeneralexpensecost || 0),
            totalEquipmentCost: acc.totalEquipmentCost + (budget?.budgetequipmentcost || 0),
            totalSubcontractorCost:
              acc.totalSubcontractorCost + (budget?.budgetsubcontractorcost || 0),
            totalSellingPrice: acc.totalSellingPrice + getBudgetSellingPrice(budget),
          };
        },
        {
          totalLaborHours: 0,
          totalDirectLaborCost: 0,
          totalIndirectLaborCost: 0,
          totalMaterialCost: 0,
          totalQuotedMaterialCost: 0,
          totalGeneralExpenseCost: 0,
          totalEquipmentCost: 0,
          totalSubcontractorCost: 0,
          totalSellingPrice: 0,
        }
      );
  };

  const calculateEstimateWithChangeOrdersTotals = (
    estimate: AssociatedEstimate.AsObject
  ): BudgetSummary => {
    const estimateBudget = calculateEstimateBudgetSummary(estimate.estimatebudget);
    const approvedChangeOrdersBudget = calculateApprovedChangeOrdersBudgetSummary(
      estimate.associatedchangeordersList || []
    );

    return {
      totalLaborHours: estimateBudget.totalLaborHours + approvedChangeOrdersBudget.totalLaborHours,
      totalDirectLaborCost:
        estimateBudget.totalDirectLaborCost + approvedChangeOrdersBudget.totalDirectLaborCost,
      totalIndirectLaborCost:
        estimateBudget.totalIndirectLaborCost + approvedChangeOrdersBudget.totalIndirectLaborCost,
      totalMaterialCost:
        estimateBudget.totalMaterialCost + approvedChangeOrdersBudget.totalMaterialCost,
      totalQuotedMaterialCost:
        estimateBudget.totalQuotedMaterialCost + approvedChangeOrdersBudget.totalQuotedMaterialCost,
      totalGeneralExpenseCost:
        estimateBudget.totalGeneralExpenseCost + approvedChangeOrdersBudget.totalGeneralExpenseCost,
      totalEquipmentCost:
        estimateBudget.totalEquipmentCost + approvedChangeOrdersBudget.totalEquipmentCost,
      totalSubcontractorCost:
        estimateBudget.totalSubcontractorCost + approvedChangeOrdersBudget.totalSubcontractorCost,
      totalSellingPrice:
        estimateBudget.totalSellingPrice + approvedChangeOrdersBudget.totalSellingPrice,
    };
  };

  const generatePrintableContent = () => {
    const printableContent = `
      <!DOCTYPE html>
      <html>
        <head>
          <title>Contract Budget Report - ${contract.contractname}</title>
          <style>
            * { margin: 0; padding: 0; box-sizing: border-box; }
            body { 
              font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif;
              line-height: 1.6;
              color: #000000;
              background: white;
              padding: 20px;
              font-size: 14px;
            }
            .header {
              text-align: center;
              margin-bottom: 30px;
              border-bottom: 3px solid #1976d2;
              padding-bottom: 20px;
            }
            .contract-title {
              font-size: 32px;
              font-weight: 700;
              color: #1976d2;
              margin-bottom: 8px;
              letter-spacing: -0.5px;
            }
            .contract-subtitle {
              font-size: 20px;
              color: #333333;
              font-weight: 500;
            }
            .contract-info {
              display: grid;
              grid-template-columns: 1fr 1fr;
              gap: 30px;
              margin-bottom: 30px;
              padding: 25px;
              background: #f8f9fa;
              border-radius: 8px;
              border: 1px solid #e0e0e0;
              page-break-inside: avoid;
              break-inside: avoid;
            }
            .info-section h3 {
              color: #1976d2;
              margin-bottom: 15px;
              font-size: 18px;
              font-weight: 600;
              border-bottom: 1px solid #e0e0e0;
              padding-bottom: 8px;
            }
            .info-item {
              margin-bottom: 12px;
            }
            .info-label {
              font-weight: 600;
              color: #333333;
              font-size: 13px;
              margin-bottom: 4px;
            }
            .info-value {
              font-size: 15px;
              color: #000000;
              font-weight: 500;
            }
            .estimate-section {
              page-break-before: always;
              margin-bottom: 40px;
            }
            .estimate-header {
              background: #1976d2;
              color: white;
              padding: 25px;
              border-radius: 8px;
              margin-bottom: 20px;
              page-break-inside: avoid;
              break-inside: avoid;
              page-break-after: avoid;
            }
            .change-orders-section {
              page-break-inside: auto;
            }
            .change-order-group {
              page-break-inside: avoid;
              break-inside: avoid;
              margin-bottom: 25px;
            }
            .budget-section {
              page-break-before: avoid;
              page-break-inside: avoid;
              break-inside: avoid;
              margin-bottom: 20px;
            }
            .estimate-title {
              font-size: 26px;
              font-weight: 700;
              margin-bottom: 10px;
            }
            .estimate-description {
              background: rgba(255,255,255,0.15);
              padding: 15px;
              border-radius: 5px;
              margin: 15px 0;
              font-style: italic;
              border-left: 3px solid rgba(255,255,255,0.4);
            }
            .estimate-details {
              display: grid;
              grid-template-columns: 1fr 1fr 1fr;
              gap: 20px;
              margin-top: 15px;
            }
            .detail-item {
              text-align: center;
            }
            .detail-label {
              font-size: 13px;
              color: rgba(255,255,255,0.9);
              margin-bottom: 5px;
              font-weight: 500;
            }
            .detail-value {
              font-size: 16px;
              font-weight: 600;
              color: white;
            }
            .budget-table {
              width: 100%;
              border-collapse: collapse;
              margin: 20px 0;
              background: white;
              box-shadow: 0 2px 8px rgba(0,0,0,0.1);
              border-radius: 8px;
              overflow: hidden;
              page-break-inside: avoid;
              break-inside: avoid;
            }
            .budget-table th {
              background: #f5f7fa;
              padding: 15px 12px;
              text-align: left;
              font-weight: 700;
              border-bottom: 2px solid #e0e0e0;
              color: #333333;
              font-size: 15px;
            }
            .budget-table th:last-child,
            .budget-table td:last-child {
              text-align: right;
            }
            .budget-table td {
              padding: 12px;
              border-bottom: 1px solid #f0f0f0;
              color: #000000;
              font-size: 14px;
              font-weight: 500;
            }
            .budget-table .total-row {
              background: #e8f4fd;
              font-weight: 700;
              color: #1976d2;
              font-size: 15px;
            }
            .budget-table .total-row td {
              padding: 15px 12px;
              border-top: 2px solid #1976d2;
            }
            .change-order {
              margin: 25px 0;
              padding: 20px;
              border: 2px solid #e0e0e0;
              border-radius: 8px;
              background: #fafafa;
              page-break-inside: avoid;
              break-inside: avoid;
            }
            .change-order-header {
              background: #f5f5f5;
              padding: 18px;
              border-radius: 6px;
              margin-bottom: 15px;
              border-left: 4px solid #1976d2;
              page-break-after: avoid;
            }
            .change-order-title {
              font-size: 18px;
              font-weight: 700;
              margin-bottom: 8px;
              color: #000000;
            }
            .change-order-description {
              background: #ffffff;
              padding: 15px;
              border-radius: 5px;
              margin: 10px 0;
              border-left: 4px solid #1976d2;
              color: #000000;
            }
            .status-chip {
              display: inline-block;
              padding: 6px 16px;
              border-radius: 20px;
              font-size: 12px;
              font-weight: 700;
              text-transform: uppercase;
              letter-spacing: 0.5px;
            }
            .total-summary {
              background: #2e7d32;
              color: white;
              padding: 30px;
              border-radius: 8px;
              margin: 30px 0;
              box-shadow: 0 4px 12px rgba(0,0,0,0.15);
              page-break-inside: avoid;
              break-inside: avoid;
            }
            .total-summary h3 {
              font-size: 24px;
              margin-bottom: 20px;
              text-align: center;
              font-weight: 700;
            }
            .total-grid {
              display: grid;
              grid-template-columns: repeat(4, 1fr);
              gap: 20px;
              margin-bottom: 20px;
            }
            .total-item {
              text-align: center;
            }
            .total-label {
              font-size: 13px;
              color: rgba(255,255,255,0.9);
              margin-bottom: 8px;
              font-weight: 500;
            }
            .total-value {
              font-size: 18px;
              font-weight: 700;
              color: white;
            }
            .grand-total {
              text-align: center;
              padding-top: 25px;
              border-top: 2px solid rgba(255,255,255,0.3);
              page-break-inside: avoid;
              break-inside: avoid;
            }
            .grand-total-label {
              font-size: 16px;
              margin-bottom: 10px;
              color: rgba(255,255,255,0.9);
              font-weight: 600;
            }
            .grand-total-value {
              font-size: 42px;
              font-weight: 900;
              color: white;
              text-shadow: 0 2px 4px rgba(0,0,0,0.2);
            }
            .breakdown {
              margin-top: 25px;
              padding-top: 25px;
              border-top: 2px solid rgba(255,255,255,0.3);
              page-break-inside: avoid;
              break-inside: avoid;
            }
            .breakdown-grid {
              display: grid;
              grid-template-columns: 1fr 1fr 1fr;
              gap: 20px;
              text-align: center;
            }
            .breakdown-item {
              color: white;
              font-weight: 600;
            }
            .breakdown-pending {
              color: rgba(255,255,255,0.7);
              font-weight: 500;
            }
            .non-approved-summary {
              background: #fff8e1;
              border-left: 5px solid #ff9800;
              padding: 25px;
              margin: 20px 0;
              border-radius: 5px;
              box-shadow: 0 2px 8px rgba(0,0,0,0.1);
              page-break-inside: avoid;
              break-inside: avoid;
            }
            .non-approved-summary h4 {
              color: #e65100;
              margin-bottom: 15px;
              font-weight: 700;
            }
            .section-title {
              font-size: 22px;
              color: #1976d2;
              margin: 30px 0 15px 0;
              font-weight: 700;
              border-bottom: 2px solid #e0e0e0;
              padding-bottom: 8px;
              page-break-after: avoid;
            }
            .page-break {
              page-break-before: always;
            }
            @media print {
              body { 
                padding: 0; 
                font-size: 12px;
                -webkit-print-color-adjust: exact;
                color-adjust: exact;
              }
              * {
                orphans: 3;
                widows: 3;
              }
              .estimate-section { 
                page-break-before: always;
                orphans: 4;
                widows: 4;
              }
              .total-summary,
              .estimate-header,
              .status-chip,
              .change-order-header,
              .budget-table th {
                -webkit-print-color-adjust: exact;
                color-adjust: exact;
              }
              .budget-table tr {
                page-break-inside: avoid;
                break-inside: avoid;
              }
              .change-order {
                orphans: 4;
                widows: 4;
              }
              .contract-title {
                font-size: 28px;
              }
              .estimate-title {
                font-size: 22px;
              }
              .grand-total-value {
                font-size: 36px;
              }
            }
          </style>
        </head>
        <body>
          <div class="header">
            <div class="contract-title">Contract Budget Report</div>
            <div class="contract-subtitle">${contract.contractname}</div>
          </div>

          <div class="contract-info">
            <div class="info-section">
              <h3>Contract Information</h3>
              <div class="info-item">
                <div class="info-label">Contract ID:</div>
                <div class="info-value">${contract.customid || 'N/A'}</div>
              </div>
              <div class="info-item">
                <div class="info-label">Contract Name:</div>
                <div class="info-value">${contract.contractname}</div>
              </div>
              <div class="info-item">
                <div class="info-label">Description:</div>
                <div class="info-value">${renderPrintHtmlContent(contract.contractdescription) || 'No description provided'}</div>
              </div>
            </div>
            <div class="info-section">
              <h3>Contract Summary</h3>
              <div class="info-item">
                <div class="info-label">Created Date:</div>
                <div class="info-value">${new Date(contract.datecreated).toLocaleDateString(
                  'en-US',
                  {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                  }
                )}</div>
              </div>
              <div class="info-item">
                <div class="info-label">Total Estimates:</div>
                <div class="info-value">${contract.associatedestimatesList?.length || 0}</div>
              </div>
              <div class="info-item">
                <div class="info-label">Total Change Orders:</div>
                <div class="info-value">${
                  contract.associatedestimatesList?.reduce(
                    (total, estimate) => total + (estimate.associatedchangeordersList?.length || 0),
                    0
                  ) || 0
                }</div>
              </div>
            </div>
          </div>

          <div class="overall-cost" style="text-align:center; margin: 30px 0; page-break-inside: avoid;">
            <h2 style="font-size: 28px; color: #6c757d; margin-bottom: 10px;">Overall Project Cost</h2>
            <div style="font-size: 18px; font-weight: 600; color: #6c757d; ">
              Total Cost: ${formatCurrency(overallCost)}
            </div>
            <div style="font-size: 28px; font-weight: 900; color: #17a2b8; margin-top:4px;">
              Selling Price: ${formatCurrency(overallSellingPrice)}
            </div>
          </div>

          ${contract.associatedestimatesList
            ?.map((estimate, index) => {
              const estimateBudget = calculateEstimateBudgetSummary(estimate.estimatebudget);
              const approvedChangeOrdersBudget = calculateApprovedChangeOrdersBudgetSummary(
                estimate.associatedchangeordersList || []
              );
              const nonApprovedChangeOrdersBudget = calculateNonApprovedChangeOrdersBudgetSummary(
                estimate.associatedchangeordersList || []
              );

              const estimateTotal = calculateEstimateWithChangeOrdersTotals(estimate);

              const estimateTotalCost =
                estimateTotal.totalDirectLaborCost +
                estimateTotal.totalIndirectLaborCost +
                estimateTotal.totalMaterialCost +
                estimateTotal.totalQuotedMaterialCost +
                estimateTotal.totalGeneralExpenseCost +
                estimateTotal.totalEquipmentCost +
                estimateTotal.totalSubcontractorCost;

              return `
                <div class="estimate-section">
                  <div class="estimate-header">
                    <div class="estimate-title">Estimate: ${estimate.name || 'Unnamed Estimate'}</div>
                    <div class="status-chip" style="background-color: ${getPrintStatusColors(estimate.estimatestatusname || 'Unknown Status').backgroundColor}; color: ${getPrintStatusColors(estimate.estimatestatusname || 'Unknown Status').color}; border: 1px solid ${getPrintStatusColors(estimate.estimatestatusname || 'Unknown Status').borderColor};">
                      ${estimate.estimatestatusname || 'Unknown Status'}
                    </div>
                    
                    ${
                      estimate.description
                        ? `<div class="estimate-description">
                            <strong>Description:</strong> ${renderPrintHtmlContent(estimate.description)}
                          </div>`
                        : ''
                    }
                    
                    <div class="estimate-details">
                      <div class="detail-item">
                        <div class="detail-label">Estimate ID</div>
                        <div class="detail-value">${estimate.customid || 'N/A'}</div>
                      </div>
                      <div class="detail-item">
                        <div class="detail-label">Master Format Division</div>
                        <div class="detail-value">${estimate.masterformatdivisionname || 'N/A'}</div>
                      </div>
                      <div class="detail-item">
                        <div class="detail-label">Due Date</div>
                        <div class="detail-value">${estimate.duedate ? new Date(estimate.duedate).toLocaleDateString() : 'N/A'}</div>
                      </div>
                    </div>
                  </div>

                  <div class="budget-section">
                    <div class="section-title">Estimate Budget Details</div>
                    <table class="budget-table">
                      <thead>
                        <tr>
                          <th>Budget Category</th>
                          <th>Amount</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr><td>Labor Hours</td><td>${formatHours(estimateBudget.totalLaborHours)}</td></tr>
                        <tr><td>Direct Labor Cost</td><td>${formatCurrency(estimateBudget.totalDirectLaborCost)}</td></tr>
                        <tr><td>Indirect Labor Cost</td><td>${formatCurrency(estimateBudget.totalIndirectLaborCost)}</td></tr>
                        <tr><td>Material Cost</td><td>${formatCurrency(estimateBudget.totalMaterialCost)}</td></tr>
                        <tr><td>Quoted Material Cost</td><td>${formatCurrency(estimateBudget.totalQuotedMaterialCost)}</td></tr>
                        <tr><td>General Expense Cost</td><td>${formatCurrency(estimateBudget.totalGeneralExpenseCost)}</td></tr>
                        <tr><td>Equipment Cost (scissor lifts, excavators, etc.)</td><td>${formatCurrency(estimateBudget.totalEquipmentCost)}</td></tr>
                        <tr><td>Subcontractor Cost</td><td>${formatCurrency(estimateBudget.totalSubcontractorCost)}</td></tr>
                        <tr class="total-row"><td><strong>Estimate Selling Price</strong></td><td><strong>${formatCurrency(estimateBudget.totalSellingPrice)}</strong></td></tr>
                      </tbody>
                    </table>
                  </div>

                  ${
                    estimate.associatedchangeordersList &&
                    estimate.associatedchangeordersList.length > 0
                      ? `
                        <div class="change-orders-section">
                          <div class="section-title">Change Orders</div>
                          ${estimate.associatedchangeordersList
                            .map(
                              changeOrder => `
                              <div class="change-order-group">
                                <div class="change-order">
                                  <div class="change-order-header">
                                    <div class="change-order-title">
                                      ${changeOrder.name || 'Unnamed Change Order'}
                                      <span class="status-chip" style="background-color: ${getPrintStatusColors(changeOrder.changeorderstatusname || 'Pending').backgroundColor}; color: ${getPrintStatusColors(changeOrder.changeorderstatusname || 'Pending').color}; border: 1px solid ${getPrintStatusColors(changeOrder.changeorderstatusname || 'Pending').borderColor};">
                                        ${changeOrder.changeorderstatusname || 'Pending'}
                                      </span>
                                    </div>
                                    ${
                                      changeOrder.description
                                        ? `<div class="change-order-description">
                                            <strong>Description:</strong> ${renderPrintHtmlContent(changeOrder.description)}
                                          </div>`
                                        : ''
                                    }
                                    <div style="margin-top: 10px; font-size: 14px; color: #333;">
                                      <strong>Change Order ID:</strong> ${changeOrder.customid || 'N/A'} | 
                                      <strong>Created:</strong> ${new Date(changeOrder.datecreated).toLocaleDateString()} | 
                                      <strong>Due:</strong> ${new Date(changeOrder.duedate).toLocaleDateString()}
                                    </div>
                                  </div>
                                  ${
                                    changeOrder.changeorderbudget
                                      ? `
                                      <table class="budget-table">
                                        <thead>
                                          <tr>
                                            <th>Budget Category</th>
                                            <th>Amount</th>
                                          </tr>
                                        </thead>
                                        <tbody>
                                          <tr><td>Labor Hours</td><td>${formatHours(changeOrder.changeorderbudget?.budgetlaborhours || 0)}</td></tr>
                                          <tr><td>Direct Labor Cost</td><td>${formatCurrency(changeOrder.changeorderbudget?.budgetdirectlaborcost || 0)}</td></tr>
                                          <tr><td>Indirect Labor Cost</td><td>${formatCurrency(changeOrder.changeorderbudget?.budgetindirectlaborcost || 0)}</td></tr>
                                          <tr><td>Material Cost</td><td>${formatCurrency(changeOrder.changeorderbudget?.budgetmaterialcost || 0)}</td></tr>
                                          <tr><td>Quoted Material Cost</td><td>${formatCurrency(changeOrder.changeorderbudget?.budgetquotedmaterialcost || 0)}</td></tr>
                                          <tr><td>General Expense Cost</td><td>${formatCurrency(changeOrder.changeorderbudget?.budgetgeneralexpensecost || 0)}</td></tr>
                                          <tr><td>Equipment Cost (scissor lifts, excavators, etc.)</td><td>${formatCurrency(changeOrder.changeorderbudget?.budgetequipmentcost || 0)}</td></tr>
                                          <tr><td>Subcontractor Cost</td><td>${formatCurrency(changeOrder.changeorderbudget?.budgetsubcontractorcost || 0)}</td></tr>
                                          <tr class="total-row"><td><strong>Change Order Selling Price</strong></td><td><strong>${formatCurrency(changeOrder.changeorderbudget?.budgetsellingprice || 0)}</strong></td></tr>
                                        </tbody>
                                      </table>
                                    `
                                      : ''
                                  }
                                </div>
                              </div>
                            `
                            )
                            .join('')}
                        </div>
                      `
                      : ''
                  }

                  <div class="total-summary">
                    <h3>ESTIMATE TOTAL (Including Approved Change Orders Only)</h3>
                    <div class="total-grid">
                      <div class="total-item">
                        <div class="total-label">Total Labor Hours</div>
                        <div class="total-value">${formatHours(estimateTotal.totalLaborHours)}</div>
                      </div>
                      <div class="total-item">
                        <div class="total-label">Direct Labor Cost</div>
                        <div class="total-value">${formatCurrency(estimateTotal.totalDirectLaborCost)}</div>
                      </div>
                      <div class="total-item">
                        <div class="total-label">Indirect Labor Cost</div>
                        <div class="total-value">${formatCurrency(estimateTotal.totalIndirectLaborCost)}</div>
                      </div>
                      <div class="total-item">
                        <div class="total-label">Material Cost</div>
                        <div class="total-value">${formatCurrency(estimateTotal.totalMaterialCost)}</div>
                      </div>
                      <div class="total-item">
                        <div class="total-label">Quoted Material Cost</div>
                        <div class="total-value">${formatCurrency(estimateTotal.totalQuotedMaterialCost)}</div>
                      </div>
                      <div class="total-item">
                        <div class="total-label">General Expense Cost</div>
                        <div class="total-value">${formatCurrency(estimateTotal.totalGeneralExpenseCost)}</div>
                      </div>
                      <div class="total-item">
                        <div class="total-label">Equipment Cost (scissor lifts, excavators, etc.)</div>
                        <div class="total-value">${formatCurrency(estimateTotal.totalEquipmentCost)}</div>
                      </div>
                      <div class="total-item">
                        <div class="total-label">Subcontractor Cost</div>
                        <div class="total-value">${formatCurrency(estimateTotal.totalSubcontractorCost)}</div>
                      </div>
                    </div>
                    <div class="grand-total">
                      <div class="grand-total-label">TOTAL COST</div>
                      <div class="grand-total-value" style="font-size:32px;">${formatCurrency(estimateTotalCost)}</div>
                      <div class="grand-total-label" style="margin-top:10px;">TOTAL SELLING PRICE</div>
                      <div class="grand-total-value">${formatCurrency(estimateTotal.totalSellingPrice)}</div>
                    </div>
                    <div class="breakdown">
                      <div class="breakdown-grid">
                        <div class="breakdown-item">
                          <strong>Estimate Only:</strong><br>
                          ${formatCurrency(estimateBudget.totalSellingPrice)}
                        </div>
                        <div class="breakdown-item">
                          <strong>Approved Change Orders:</strong><br>
                          ${formatCurrency(approvedChangeOrdersBudget.totalSellingPrice)}
                        </div>
                        <div class="breakdown-pending">
                          <strong>Non-Approved Change Orders:</strong><br>
                          ${formatCurrency(nonApprovedChangeOrdersBudget.totalSellingPrice)}
                        </div>
                      </div>
                    </div>
                  </div>

                  ${
                    nonApprovedChangeOrdersBudget.totalSellingPrice > 0
                      ? `
                      <div class="non-approved-summary">
                        <h4>Non-Approved Change Orders Summary</h4>
                        <p style="margin-bottom: 15px; color: #e65100; font-weight: 500;">These change orders are not included in the estimate total above.</p>
                        <div style="display: grid; grid-template-columns: repeat(4, 1fr); gap: 15px;">
                          <div>
                            <strong>Pending Hours:</strong><br>
                            ${formatHours(nonApprovedChangeOrdersBudget.totalLaborHours)}
                          </div>
                          <div>
                            <strong>Pending Labor Cost:</strong><br>
                            ${formatCurrency(
                              nonApprovedChangeOrdersBudget.totalDirectLaborCost +
                                nonApprovedChangeOrdersBudget.totalIndirectLaborCost
                            )}
                          </div>
                          <div>
                            <strong>Pending Material Cost:</strong><br>
                            ${formatCurrency(
                              nonApprovedChangeOrdersBudget.totalMaterialCost +
                                nonApprovedChangeOrdersBudget.totalQuotedMaterialCost
                            )}
                          </div>
                          <div>
                            <strong>Pending Selling Price:</strong><br>
                            ${formatCurrency(nonApprovedChangeOrdersBudget.totalSellingPrice)}
                          </div>
                        </div>
                      </div>
                      `
                      : ''
                  }
                </div>
              `;
            })
            .join('')}
        </body>
      </html>
    `;
    return printableContent;
  };

  const handlePrint = () => {
    const printContent = generatePrintableContent();
    const printWindow = window.open('', '_blank');
    if (printWindow) {
      printWindow.document.write(printContent);
      printWindow.document.close();
      printWindow.focus();
      setTimeout(() => {
        printWindow.print();
        printWindow.close();
      }, 250);
    }
  };

  const handleExport = () => {
    const printContent = generatePrintableContent();
    const blob = new Blob([printContent], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `Contract_Budget_Report_${contract.contractname?.replace(/[^a-zA-Z0-9]/g, '_')}_${new Date().toISOString().split('T')[0]}.html`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const calculateOverallProjectTotals = (
    contractData: Contract.AsObject
  ): { sellingPrice: number; totalCost: number } => {
    let sellingPrice = 0;
    let totalCost = 0;

    contractData.associatedestimatesList?.forEach(estimate => {
      const eb = estimate.estimatebudget;
      sellingPrice += getBudgetSellingPrice(eb);
      totalCost += getBudgetCost(eb);

      estimate.associatedchangeordersList?.forEach(co => {
        if (co.changeorderstatusid !== acceptedEstimateId) return;
        const cb = co.changeorderbudget;
        sellingPrice += getBudgetSellingPrice(cb);
        totalCost += getBudgetCost(cb);
      });
    });

    return { sellingPrice, totalCost };
  };

  const { sellingPrice: overallSellingPrice, totalCost: overallCost } =
    calculateOverallProjectTotals(contract);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          minHeight: '80vh',
          backgroundColor: theme.palette.background.paper,
          '@media print': {
            boxShadow: 'none',
            margin: 0,
            padding: 0,
            maxWidth: 'none',
            height: 'auto',
          },
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          borderBottom: `1px solid ${theme.palette.divider}`,
          pb: 2,
          '@media print': {
            display: 'none',
          },
        }}
      >
        <Typography variant="h5" component="div" fontWeight={600}>
          Contract Budget Report
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <IconButton onClick={handlePrint} color="primary" aria-label="Print report">
            <PrintIcon />
          </IconButton>
          <IconButton onClick={handleExport} color="primary" aria-label="Export report">
            <GetAppIcon />
          </IconButton>
          <IconButton onClick={onClose} aria-label="Close report">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: 3, '@media print': { p: 1 } }}>
        {/* Contract Header Information */}
        <Card sx={{ mb: 4, backgroundColor: theme.palette.background.default }}>
          <CardContent>
            <Typography variant="h4" fontWeight={700} gutterBottom align="center" color="primary">
              Contract Budget Report
            </Typography>

            <Grid container spacing={3} sx={{ mt: 2 }}>
              <Grid item xs={12} md={6}>
                <Typography variant="h6" fontWeight={600} gutterBottom>
                  Contract Information
                </Typography>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Contract ID:
                  </Typography>
                  <Typography variant="h6" fontWeight={500}>
                    {contract.customid || 'N/A'}
                  </Typography>
                </Box>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Contract Name:
                  </Typography>
                  <Typography variant="h6" fontWeight={500}>
                    {contract.contractname}
                  </Typography>
                </Box>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Description:
                  </Typography>
                  <Typography variant="body1" component="div">
                    {renderHtmlContent(contract.contractdescription) || 'No description provided'}
                  </Typography>
                </Box>
              </Grid>

              <Grid item xs={12} md={6}>
                <Typography variant="h6" fontWeight={600} gutterBottom>
                  Contract Summary
                </Typography>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Created Date:
                  </Typography>
                  <Typography variant="body1" fontWeight={500}>
                    {new Date(contract.datecreated).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                    })}
                  </Typography>
                </Box>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Total Estimates:
                  </Typography>
                  <Typography variant="body1" fontWeight={500}>
                    {contract.associatedestimatesList?.length || 0}
                  </Typography>
                </Box>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Total Change Orders:
                  </Typography>
                  <Typography variant="body1" fontWeight={500}>
                    {contract.associatedestimatesList?.reduce(
                      (total, estimate) =>
                        total + (estimate.associatedchangeordersList?.length || 0),
                      0
                    ) || 0}
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Overall Project Cost */}
        <Card
          sx={{
            mb: 4,
            backgroundColor:
              theme.palette.mode === 'dark'
                ? alpha(theme.palette.secondary.main, 0.15)
                : theme.palette.secondary.light,
          }}
        >
          <CardContent sx={{ textAlign: 'center' }}>
            <Typography variant="h5" fontWeight={600} gutterBottom color="secondary">
              Overall Project Totals
            </Typography>
            <Box>
              <Typography variant="body1" color="text.secondary">
                Total Cost
              </Typography>
              <Typography variant="h4" fontWeight={700} gutterBottom>
                {formatCurrency(overallCost)}
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Total Selling Price
              </Typography>
              <Typography variant="h4" fontWeight={700}>
                {formatCurrency(overallSellingPrice)}
              </Typography>
            </Box>
          </CardContent>
        </Card>

        {/* Each Estimate with its Change Orders - Each on a new page */}
        {contract.associatedestimatesList?.map((estimate, estimateIndex) => {
          const estimateBudget = calculateEstimateBudgetSummary(estimate.estimatebudget);
          const approvedChangeOrdersBudget = calculateApprovedChangeOrdersBudgetSummary(
            estimate.associatedchangeordersList || []
          );
          const nonApprovedChangeOrdersBudget = calculateNonApprovedChangeOrdersBudgetSummary(
            estimate.associatedchangeordersList || []
          );
          const estimateTotal = calculateEstimateWithChangeOrdersTotals(estimate);

          const estimateTotalCost =
            estimateTotal.totalDirectLaborCost +
            estimateTotal.totalIndirectLaborCost +
            estimateTotal.totalMaterialCost +
            estimateTotal.totalQuotedMaterialCost +
            estimateTotal.totalGeneralExpenseCost +
            estimateTotal.totalEquipmentCost +
            estimateTotal.totalSubcontractorCost;

          return (
            <Box
              key={estimate.estimateid}
              sx={{
                pageBreakBefore: estimateIndex > 0 ? 'always' : 'auto',
                '@media print': {
                  pageBreakBefore: estimateIndex > 0 ? 'always' : 'auto',
                },
                mb: 4,
              }}
            >
              {/* Estimate Header */}
              <Card sx={{ mb: 3, backgroundColor: theme.palette.primary.main, color: 'white' }}>
                <CardContent>
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      mb: 2,
                    }}
                  >
                    <Typography variant="h4" fontWeight={600}>
                      Estimate: {estimate.name || 'Unnamed Estimate'}
                    </Typography>
                    <Chip
                      label={estimate.estimatestatusname || 'Unknown Status'}
                      size="medium"
                      sx={{
                        ...getStatusChipProps(
                          estimate.estimatestatusname || 'Unknown Status',
                          theme
                        ),
                        fontWeight: 500,
                      }}
                    />
                  </Box>

                  {/* Estimate Description */}
                  {estimate.description && (
                    <Box
                      sx={{
                        mb: 3,
                        p: 2,
                        backgroundColor: 'rgba(255,255,255,0.1)',
                        borderRadius: 1,
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={{ opacity: 0.9, fontStyle: 'italic' }}
                        gutterBottom
                      >
                        Description:
                      </Typography>
                      <Typography variant="body1" fontWeight={500} component="div">
                        {renderHtmlContent(estimate.description)}
                      </Typography>
                    </Box>
                  )}

                  <Grid container spacing={3}>
                    <Grid item xs={12} sm={4}>
                      <Typography variant="body2" sx={{ opacity: 0.9 }} gutterBottom>
                        Estimate ID:
                      </Typography>
                      <Typography variant="h6" fontWeight={500}>
                        {estimate.customid || 'N/A'}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <Typography variant="body2" sx={{ opacity: 0.9 }} gutterBottom>
                        Master Format Division:
                      </Typography>
                      <Typography variant="h6" fontWeight={500}>
                        {estimate.masterformatdivisionname || 'N/A'}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <Typography variant="body2" sx={{ opacity: 0.9 }} gutterBottom>
                        Due Date:
                      </Typography>
                      <Typography variant="h6" fontWeight={500}>
                        {estimate.duedate ? new Date(estimate.duedate).toLocaleDateString() : 'N/A'}
                      </Typography>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>

              {/* Estimate Budget Details */}
              <Card sx={{ mb: 3 }}>
                <CardContent>
                  <Typography variant="h5" fontWeight={600} gutterBottom color="primary">
                    Estimate Budget Details
                  </Typography>
                  <TableContainer
                    component={Paper}
                    elevation={2}
                    sx={{
                      backgroundColor: theme.palette.background.paper,
                      '& .MuiPaper-root': {
                        backgroundColor: theme.palette.background.paper,
                      },
                      '& .MuiTableCell-root': {
                        backgroundColor: theme.palette.background.paper,
                      },
                    }}
                  >
                    <Table>
                      <TableHead>
                        <TableRow
                          sx={{
                            backgroundColor:
                              theme.palette.mode === 'dark'
                                ? alpha(theme.palette.primary.main, 0.2)
                                : theme.palette.grey[100],
                          }}
                        >
                          <TableCell
                            sx={{
                              fontWeight: 700,
                              fontSize: '1.1rem',
                              color:
                                theme.palette.mode === 'dark'
                                  ? theme.palette.primary.light
                                  : theme.palette.text.primary,
                            }}
                          >
                            Budget Category
                          </TableCell>
                          <TableCell
                            align="right"
                            sx={{
                              fontWeight: 700,
                              fontSize: '1.1rem',
                              color:
                                theme.palette.mode === 'dark'
                                  ? theme.palette.primary.light
                                  : theme.palette.text.primary,
                            }}
                          >
                            Amount
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        <TableRow>
                          <TableCell
                            sx={{
                              fontSize: '1rem',
                              backgroundColor: 'transparent',
                            }}
                          >
                            Labor Hours
                          </TableCell>
                          <TableCell
                            align="right"
                            sx={{
                              fontSize: '1rem',
                              backgroundColor: 'transparent',
                            }}
                          >
                            {formatHours(estimateBudget.totalLaborHours)}
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell
                            sx={{
                              fontSize: '1rem',
                              backgroundColor: 'transparent',
                            }}
                          >
                            Direct Labor Cost
                          </TableCell>
                          <TableCell
                            align="right"
                            sx={{
                              fontSize: '1rem',
                              backgroundColor: 'transparent',
                            }}
                          >
                            {formatCurrency(estimateBudget.totalDirectLaborCost)}
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell
                            sx={{
                              fontSize: '1rem',
                              backgroundColor: 'transparent',
                            }}
                          >
                            Indirect Labor Cost
                          </TableCell>
                          <TableCell
                            align="right"
                            sx={{
                              fontSize: '1rem',
                              backgroundColor: 'transparent',
                            }}
                          >
                            {formatCurrency(estimateBudget.totalIndirectLaborCost)}
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell
                            sx={{
                              fontSize: '1rem',
                              backgroundColor: 'transparent',
                            }}
                          >
                            Material Cost
                          </TableCell>
                          <TableCell
                            align="right"
                            sx={{
                              fontSize: '1rem',
                              backgroundColor: 'transparent',
                            }}
                          >
                            {formatCurrency(estimateBudget.totalMaterialCost)}
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell
                            sx={{
                              fontSize: '1rem',
                              backgroundColor: 'transparent',
                            }}
                          >
                            Quoted Material Cost
                          </TableCell>
                          <TableCell
                            align="right"
                            sx={{
                              fontSize: '1rem',
                              backgroundColor: 'transparent',
                            }}
                          >
                            {formatCurrency(estimateBudget.totalQuotedMaterialCost)}
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell
                            sx={{
                              fontSize: '1rem',
                              backgroundColor: 'transparent',
                            }}
                          >
                            General Expense Cost
                          </TableCell>
                          <TableCell
                            align="right"
                            sx={{
                              fontSize: '1rem',
                              backgroundColor: 'transparent',
                            }}
                          >
                            {formatCurrency(estimateBudget.totalGeneralExpenseCost)}
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell
                            sx={{
                              fontSize: '1rem',
                              backgroundColor: 'transparent',
                            }}
                          >
                            Equipment Cost (scissor lifts, excavators, etc.)
                          </TableCell>
                          <TableCell
                            align="right"
                            sx={{
                              fontSize: '1rem',
                              backgroundColor: 'transparent',
                            }}
                          >
                            {formatCurrency(estimateBudget.totalEquipmentCost)}
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell
                            sx={{
                              fontSize: '1rem',
                              backgroundColor: 'transparent',
                            }}
                          >
                            Subcontractor Cost
                          </TableCell>
                          <TableCell
                            align="right"
                            sx={{
                              fontSize: '1rem',
                              backgroundColor: 'transparent',
                            }}
                          >
                            {formatCurrency(estimateBudget.totalSubcontractorCost)}
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: theme.palette.primary.light }}>
                          <TableCell sx={{ fontWeight: 700, fontSize: '1.1rem' }}>
                            Estimate Selling Price
                          </TableCell>
                          <TableCell align="right" sx={{ fontWeight: 700, fontSize: '1.1rem' }}>
                            {formatCurrency(estimateBudget.totalSellingPrice)}
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </CardContent>
              </Card>

              {/* Change Orders Section */}
              {estimate.associatedchangeordersList &&
                estimate.associatedchangeordersList.length > 0 && (
                  <Card sx={{ mb: 3 }}>
                    <CardContent>
                      <Typography variant="h5" fontWeight={600} gutterBottom color="primary">
                        Change Orders ({estimate.associatedchangeordersList.length})
                      </Typography>

                      {estimate.associatedchangeordersList.map((changeOrder, coIndex) => (
                        <Box key={changeOrder.changeorderid} sx={{ mb: 4 }}>
                          <Box
                            sx={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              mb: 2,
                              p: 2,
                              backgroundColor:
                                theme.palette.mode === 'dark'
                                  ? alpha(theme.palette.primary.main, 0.1)
                                  : theme.palette.grey[50],
                              borderRadius: 1,
                              border:
                                theme.palette.mode === 'dark'
                                  ? `1px solid ${alpha(theme.palette.primary.main, 0.3)}`
                                  : 'none',
                            }}
                          >
                            <Typography variant="h6" fontWeight={600}>
                              Change Order #{coIndex + 1}:{' '}
                              {changeOrder.name || 'Unnamed Change Order'}
                            </Typography>
                            <Chip
                              label={changeOrder.changeorderstatusname || 'Unknown Status'}
                              size="medium"
                              sx={{
                                ...getStatusChipProps(
                                  changeOrder.changeorderstatusname || 'Unknown Status',
                                  theme
                                ),
                                fontWeight: 500,
                              }}
                            />
                          </Box>

                          {/* Change Order Description */}
                          {changeOrder.description && (
                            <Box
                              sx={{
                                mb: 2,
                                p: 2,
                                backgroundColor:
                                  theme.palette.mode === 'dark'
                                    ? alpha(theme.palette.background.paper, 0.8)
                                    : theme.palette.grey[100],
                                borderRadius: 1,
                                border:
                                  theme.palette.mode === 'dark'
                                    ? `1px solid ${theme.palette.divider}`
                                    : 'none',
                              }}
                            >
                              <Typography
                                variant="body2"
                                color="text.secondary"
                                gutterBottom
                                sx={{ fontWeight: 600 }}
                              >
                                Description:
                              </Typography>
                              <Typography variant="body1" component="div">
                                {renderHtmlContent(changeOrder.description)}
                              </Typography>
                            </Box>
                          )}

                          <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                            <strong>Change Order ID:</strong> {changeOrder.customid || 'N/A'} |
                            <strong> Created:</strong>{' '}
                            {new Date(changeOrder.datecreated).toLocaleDateString()} |
                            <strong> Due:</strong>{' '}
                            {new Date(changeOrder.duedate).toLocaleDateString()}
                          </Typography>

                          {changeOrder.changeorderbudget && (
                            <TableContainer
                              component={Paper}
                              sx={{
                                mb: 3,
                                backgroundColor: theme.palette.background.paper,
                                '& .MuiPaper-root': {
                                  backgroundColor: theme.palette.background.paper,
                                },
                                '& .MuiTableCell-root': {
                                  backgroundColor: theme.palette.background.paper,
                                },
                              }}
                              elevation={1}
                            >
                              <Table>
                                <TableHead>
                                  <TableRow
                                    sx={{
                                      backgroundColor:
                                        theme.palette.mode === 'dark'
                                          ? alpha(theme.palette.primary.main, 0.2)
                                          : theme.palette.grey[100],
                                    }}
                                  >
                                    <TableCell
                                      sx={{
                                        fontWeight: 600,
                                        color:
                                          theme.palette.mode === 'dark'
                                            ? theme.palette.primary.light
                                            : theme.palette.text.primary,
                                      }}
                                    >
                                      Budget Category
                                    </TableCell>
                                    <TableCell
                                      align="right"
                                      sx={{
                                        fontWeight: 600,
                                        color:
                                          theme.palette.mode === 'dark'
                                            ? theme.palette.primary.light
                                            : theme.palette.text.primary,
                                      }}
                                    >
                                      Amount
                                    </TableCell>
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  <TableRow>
                                    <TableCell>Labor Hours</TableCell>
                                    <TableCell align="right">
                                      {formatHours(
                                        changeOrder.changeorderbudget?.budgetlaborhours || 0
                                      )}
                                    </TableCell>
                                  </TableRow>
                                  <TableRow>
                                    <TableCell>Direct Labor Cost</TableCell>
                                    <TableCell align="right">
                                      {formatCurrency(
                                        changeOrder.changeorderbudget?.budgetdirectlaborcost || 0
                                      )}
                                    </TableCell>
                                  </TableRow>
                                  <TableRow>
                                    <TableCell>Indirect Labor Cost</TableCell>
                                    <TableCell align="right">
                                      {formatCurrency(
                                        changeOrder.changeorderbudget?.budgetindirectlaborcost || 0
                                      )}
                                    </TableCell>
                                  </TableRow>
                                  <TableRow>
                                    <TableCell>Material Cost</TableCell>
                                    <TableCell align="right">
                                      {formatCurrency(
                                        changeOrder.changeorderbudget?.budgetmaterialcost || 0
                                      )}
                                    </TableCell>
                                  </TableRow>
                                  <TableRow>
                                    <TableCell>Quoted Material Cost</TableCell>
                                    <TableCell align="right">
                                      {formatCurrency(
                                        changeOrder.changeorderbudget?.budgetquotedmaterialcost || 0
                                      )}
                                    </TableCell>
                                  </TableRow>
                                  <TableRow>
                                    <TableCell>General Expense Cost</TableCell>
                                    <TableCell align="right">
                                      {formatCurrency(
                                        changeOrder.changeorderbudget?.budgetgeneralexpensecost || 0
                                      )}
                                    </TableCell>
                                  </TableRow>
                                  <TableRow>
                                    <TableCell>Equipment Cost</TableCell>
                                    <TableCell align="right">
                                      {formatCurrency(
                                        changeOrder.changeorderbudget?.budgetequipmentcost || 0
                                      )}
                                    </TableCell>
                                  </TableRow>
                                  <TableRow>
                                    <TableCell>Subcontractor Cost</TableCell>
                                    <TableCell align="right">
                                      {formatCurrency(
                                        changeOrder.changeorderbudget?.budgetsubcontractorcost || 0
                                      )}
                                    </TableCell>
                                  </TableRow>
                                  <TableRow sx={{ backgroundColor: theme.palette.warning.light }}>
                                    <TableCell sx={{ fontWeight: 600 }}>
                                      Change Order Selling Price
                                    </TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 600 }}>
                                      {formatCurrency(
                                        changeOrder.changeorderbudget?.budgetsellingprice || 0
                                      )}
                                    </TableCell>
                                  </TableRow>
                                </TableBody>
                              </Table>
                            </TableContainer>
                          )}
                        </Box>
                      ))}
                    </CardContent>
                  </Card>
                )}

              {/* Estimate + Change Orders Combined Total */}
              <Card sx={{ backgroundColor: theme.palette.success.main, color: 'white', mb: 4 }}>
                <CardContent>
                  <Typography variant="h5" fontWeight={700} gutterBottom>
                    ESTIMATE TOTAL (Including Approved Change Orders Only)
                  </Typography>

                  <Grid container spacing={2} sx={{ mt: 1 }}>
                    <Grid item xs={12} sm={6} md={4} lg={3}>
                      <Typography variant="body2" sx={{ opacity: 0.9 }} gutterBottom>
                        Total Labor Hours
                      </Typography>
                      <Typography variant="h6" fontWeight={700}>
                        {formatHours(estimateTotal.totalLaborHours)}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6} md={4} lg={3}>
                      <Typography variant="body2" sx={{ opacity: 0.9 }} gutterBottom>
                        Direct Labor Cost
                      </Typography>
                      <Typography variant="h6" fontWeight={700}>
                        {formatCurrency(estimateTotal.totalDirectLaborCost)}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6} md={4} lg={3}>
                      <Typography variant="body2" sx={{ opacity: 0.9 }} gutterBottom>
                        Indirect Labor Cost
                      </Typography>
                      <Typography variant="h6" fontWeight={700}>
                        {formatCurrency(estimateTotal.totalIndirectLaborCost)}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6} md={4} lg={3}>
                      <Typography variant="body2" sx={{ opacity: 0.9 }} gutterBottom>
                        Material Cost
                      </Typography>
                      <Typography variant="h6" fontWeight={700}>
                        {formatCurrency(estimateTotal.totalMaterialCost)}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6} md={4} lg={3}>
                      <Typography variant="body2" sx={{ opacity: 0.9 }} gutterBottom>
                        Quoted Material Cost
                      </Typography>
                      <Typography variant="h6" fontWeight={700}>
                        {formatCurrency(estimateTotal.totalQuotedMaterialCost)}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6} md={4} lg={3}>
                      <Typography variant="body2" sx={{ opacity: 0.9 }} gutterBottom>
                        General Expense Cost
                      </Typography>
                      <Typography variant="h6" fontWeight={700}>
                        {formatCurrency(estimateTotal.totalGeneralExpenseCost)}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6} md={4} lg={3}>
                      <Typography variant="body2" sx={{ opacity: 0.9 }} gutterBottom>
                        Equipment Cost
                      </Typography>
                      <Typography variant="h6" fontWeight={700}>
                        {formatCurrency(estimateTotal.totalEquipmentCost)}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6} md={4} lg={3}>
                      <Typography variant="body2" sx={{ opacity: 0.9 }} gutterBottom>
                        Subcontractor Cost
                      </Typography>
                      <Typography variant="h6" fontWeight={700}>
                        {formatCurrency(estimateTotal.totalSubcontractorCost)}
                      </Typography>
                    </Grid>
                  </Grid>

                  {/* Totals - Cost vs Selling Price */}
                  <Box
                    sx={{
                      mt: 3,
                      pt: 2,
                      borderTop: '1px solid rgba(255,255,255,0.3)',
                      textAlign: 'center',
                    }}
                  >
                    <Typography variant="body1" sx={{ opacity: 0.9 }} gutterBottom>
                      TOTAL COST
                    </Typography>
                    <Typography variant="h4" fontWeight={800} gutterBottom>
                      {formatCurrency(estimateTotalCost)}
                    </Typography>
                    <Typography variant="body1" sx={{ opacity: 0.9 }} gutterBottom>
                      TOTAL SELLING PRICE
                    </Typography>
                    <Typography variant="h3" fontWeight={900}>
                      {formatCurrency(estimateTotal.totalSellingPrice)}
                    </Typography>
                  </Box>

                  {/* Detailed Breakdown */}
                  <Box sx={{ mt: 3, pt: 2, borderTop: '1px solid rgba(255,255,255,0.3)' }}>
                    <Typography variant="h6" sx={{ opacity: 0.9, mb: 2 }}>
                      Breakdown:
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={4}>
                        <Typography variant="body1" fontWeight={600}>
                          Estimate Only: {formatCurrency(estimateBudget.totalSellingPrice)}
                        </Typography>
                      </Grid>
                      <Grid item xs={4}>
                        <Typography variant="body1" fontWeight={600}>
                          Approved Change Orders:{' '}
                          {formatCurrency(approvedChangeOrdersBudget.totalSellingPrice)}
                        </Typography>
                      </Grid>
                      <Grid item xs={4}>
                        <Typography variant="body1" fontWeight={600} sx={{ opacity: 0.7 }}>
                          Non-Approved Change Orders:{' '}
                          {formatCurrency(nonApprovedChangeOrdersBudget.totalSellingPrice)}
                        </Typography>
                      </Grid>
                    </Grid>
                  </Box>
                </CardContent>
              </Card>

              {/* Non-Approved Change Orders Summary */}
              {nonApprovedChangeOrdersBudget.totalSellingPrice > 0 && (
                <Card sx={{ backgroundColor: theme.palette.warning.light, mb: 4 }}>
                  <CardContent>
                    <Typography variant="h6" fontWeight={600} gutterBottom>
                      Non-Approved Change Orders Summary
                    </Typography>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      These change orders are not included in the estimate total above.
                    </Typography>
                    <Grid container spacing={2} sx={{ mt: 1 }}>
                      <Grid item xs={6} sm={3}>
                        <Typography variant="body2" gutterBottom>
                          Pending Hours
                        </Typography>
                        <Typography variant="h6" fontWeight={600}>
                          {formatHours(nonApprovedChangeOrdersBudget.totalLaborHours)}
                        </Typography>
                      </Grid>
                      <Grid item xs={6} sm={3}>
                        <Typography variant="body2" gutterBottom>
                          Pending Labor Cost
                        </Typography>
                        <Typography variant="h6" fontWeight={600}>
                          {formatCurrency(
                            nonApprovedChangeOrdersBudget.totalDirectLaborCost +
                              nonApprovedChangeOrdersBudget.totalIndirectLaborCost
                          )}
                        </Typography>
                      </Grid>
                      <Grid item xs={6} sm={3}>
                        <Typography variant="body2" gutterBottom>
                          Pending Material Cost
                        </Typography>
                        <Typography variant="h6" fontWeight={600}>
                          {formatCurrency(
                            nonApprovedChangeOrdersBudget.totalMaterialCost +
                              nonApprovedChangeOrdersBudget.totalQuotedMaterialCost
                          )}
                        </Typography>
                      </Grid>
                      <Grid item xs={6} sm={3}>
                        <Typography variant="body2" gutterBottom>
                          Pending Selling Price
                        </Typography>
                        <Typography variant="h6" fontWeight={600}>
                          {formatCurrency(nonApprovedChangeOrdersBudget.totalSellingPrice)}
                        </Typography>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              )}
            </Box>
          );
        })}
      </DialogContent>

      <DialogActions
        sx={{
          p: 3,
          borderTop: `1px solid ${theme.palette.divider}`,
          '@media print': { display: 'none' },
        }}
      >
        <Button onClick={onClose} color="primary">
          Close
        </Button>
        <Button onClick={handlePrint} variant="contained" startIcon={<PrintIcon />}>
          Print Report
        </Button>
      </DialogActions>
    </Dialog>
  );
};
